import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Fab,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Mic,
  MicOff,
  Send,
  CameraAlt,
  PhotoCamera,
  PhotoLibrary,
  Cancel,
} from '@mui/icons-material';
import { useConversation } from '../contexts/ConversationContext';
import { useAppConfig } from '../contexts/ConfigContext';
import AudioWaveform from './AudioWaveform';
import CameraModal from './CameraModal';
import { getTextDirectionStyles, getLocalizedPlaceholder } from '../utils/textDirection';
import { validateImageFile, fileToBase64, IMAGE_SEARCH_CONSTRAINTS } from '../types/imageSearch';
import { isMobileOrTablet } from '../utils/deviceDetection';

const AudioControls: React.FC = () => {  const { 
    isRecording, 
    isProcessing, 
    isConnected, 
    isAudioPlaying,
    messages, 
    startRecording, 
    stopRecording, 
    sendMessage,
    sendImageSearch
  } = useConversation();
  const { config } = useAppConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [textMessage, setTextMessage] = useState('');
  const [imageMenuAnchor, setImageMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedImage, setSelectedImage] = useState<{ data: string; preview: string } | null>(null);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleStartRecording = async () => {
    try {
      await startRecording();
    } catch (error) {
      console.error('Failed to start recording:', error);
    }
  };

  const handleStopRecording = async () => {
    try {
      await stopRecording();
    } catch (error) {
      console.error('Failed to stop recording:', error);
    }
  };  const handleSendTextMessage = () => {
    // If image is selected, send image search
    if (selectedImage) {
      sendImageSearch(selectedImage.data, textMessage.trim() || undefined, {
        detailLevel: IMAGE_SEARCH_CONSTRAINTS.DEFAULT_DETAIL_LEVEL,
      });
      setSelectedImage(null);
      setTextMessage('');
      return;
    }
    
    // Otherwise send regular text message
    if (textMessage.trim() && !isProcessing) {
      sendMessage(textMessage.trim());
      setTextMessage('');
    }
  };
  
  const handleRemoveImage = () => {
    if (selectedImage?.preview) {
      URL.revokeObjectURL(selectedImage.preview);
    }
    setSelectedImage(null);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendTextMessage();
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate image file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    try {
      // Convert to base64
      const base64Data = await fileToBase64(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      
      // Store image for sending later
      setSelectedImage({ data: base64Data, preview: previewUrl });

      // Clear the file input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    }
  };

  const handleImageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setImageMenuAnchor(event.currentTarget);
  };

  const handleImageMenuClose = () => {
    setImageMenuAnchor(null);
  };

  const handleBrowseFiles = () => {
    handleImageMenuClose();
    fileInputRef.current?.click();
  };

  const handleCameraClick = () => {
    handleImageMenuClose();
    
    // Use device detection to choose camera method
    if (isMobileOrTablet()) {
      // Mobile/Tablet: Use native camera capture via file input
      // This opens the device's native camera app for best UX
      cameraInputRef.current?.click();
    } else {
      // Desktop: Open camera modal with live preview
      setCameraModalOpen(true);
    }
  };

  /**
   * Handle image captured from camera modal (desktop)
   */
  const handleCameraCapture = (imageData: string, preview: string) => {
    setSelectedImage({ data: imageData, preview: preview });
    setCameraModalOpen(false);
  };

  // Disable recording button when:
  // 1. Not connected or processing
  // 2. Assistant message is streaming
  // 3. User audio message is being transcribed
  // 4. Any audio is currently playing
  // 5. TTS is being generated for any message
  // 6. Image is selected (before or during processing)
  // 7. Image search is being processed
  const isRecordingDisabled = !isConnected || isProcessing || isAudioPlaying || selectedImage !== null ||
    messages.some(msg => 
      (msg.type === 'assistant' && msg.isStreaming) || 
      (msg.type === 'user' && msg.messageType === 'audio' && msg.isStreaming) ||
      (msg.type === 'user' && msg.messageType === 'image_search' && msg.isStreaming) ||
      msg.isGeneratingTTS    );

  // Disable other controls (image button, text input, send) when:
  // 1. Not connected or processing
  // 2. Assistant message is streaming
  // 3. User audio message is being transcribed
  // 4. Any audio is currently playing
  // 5. TTS is being generated for any message
  // Note: Does NOT include image selection - user can still type and send
  const isDisabled = !isConnected || isProcessing || isAudioPlaying ||
    messages.some(msg => 
      (msg.type === 'assistant' && msg.isStreaming) || 
      (msg.type === 'user' && msg.messageType === 'audio' && msg.isStreaming) ||
      msg.isGeneratingTTS    );

  return (
    <Box>      {/* Hidden file input for browsing files */}
      <input
        ref={fileInputRef}
        type="file"
        accept={IMAGE_SEARCH_CONSTRAINTS.SUPPORTED_FORMATS.map(f => `.${f}`).join(',')}
        style={{ display: 'none' }}
        onChange={handleImageUpload}
        aria-label="Upload image file"
      />
      
      {/* Hidden camera input for native mobile camera capture */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleImageUpload}
        aria-label="Take photo with camera"
      />
      
      {/* Camera modal for desktop browser camera preview */}
      <CameraModal
        open={cameraModalOpen}
        onClose={() => setCameraModalOpen(false)}
        onCapture={handleCameraCapture}
      />

      {/* Text Input with Recording and Image Upload Buttons */}
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-end', mb: 1 }}>{/* Recording Button */}
        <Tooltip title={
          !isConnected ? 'Not connected' :
          isProcessing ? 'Processing...' :
          isAudioPlaying ? 'Audio is playing...' :
          selectedImage !== null ? 'Remove image to enable recording' :
          messages.some(msg => msg.type === 'assistant' && msg.isStreaming) ? 'Assistant is responding...' :
          messages.some(msg => msg.type === 'user' && msg.messageType === 'audio' && msg.isStreaming) ? 'Transcribing audio...' :
          messages.some(msg => msg.type === 'user' && msg.messageType === 'image_search' && msg.isStreaming) ? 'Processing image search...' :
          messages.some(msg => msg.isGeneratingTTS) ? 'Generating audio...' :
          isRecording ? 'Stop Recording' : (config?.ui_tooltips?.start_recording_tooltip || 'Start Recording')
        }>
          <span>            <Fab
              className={`record-button ${isRecording ? 'recording' : ''}`}
              disabled={isRecordingDisabled}
              onClick={isRecording ? handleStopRecording : handleStartRecording}
              sx={{                width: 48,
                height: 48,
                fontSize: '1.2rem',
                position: 'relative',
                flexShrink: 0,
                backgroundColor: isRecording 
                  ? (config?.ui_theme?.recording_button_recording_background || '#f44336')
                  : (config?.ui_theme?.recording_button_background || '#1976d2'),
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: isRecording
                    ? `${config?.ui_theme?.recording_button_recording_hover_background || '#d32f2f'} !important`
                    : `${config?.ui_theme?.recording_button_hover_background || '#1565c0'} !important`,
                },
                '&.Mui-disabled': {
                  backgroundColor: '#cccccc',
                  color: '#666666',
                },
              }}>
              {isRecording ? (
                <>
                  <MicOff />
                  <AudioWaveform isPlaying={isRecording} size="small" color="white" />
                </>
              ) : (
                <Mic />
              )}
            </Fab>
          </span>
        </Tooltip>

        {/* Image Upload Button with Menu */}
        {config?.ui_behavior?.enable_image_search_button !== false && (<>
        <Tooltip title={
          !isConnected ? 'Not connected' :
          isProcessing ? 'Processing...' :
          (config?.ui_tooltips?.image_search_button_tooltip || 'Upload an image to search for similar products')
        }>
          <span>
            <IconButton
              onClick={handleImageMenuOpen}
              disabled={isDisabled}
              sx={{
                width: 48,
                height: 48,
                backgroundColor: config?.ui_theme?.image_search_button_background || '#0f3052',
                color: config?.ui_theme?.image_search_button_icon_color || '#ffffff',
                border: `1px solid ${config?.ui_theme?.image_search_button_border || 'rgba(15, 48, 82, 0.12)'}`,
                '&:hover': {
                  backgroundColor: `${config?.ui_theme?.image_search_button_hover_background || '#2569ad'} !important`,
                },
                '&.Mui-disabled': {
                  backgroundColor: config?.ui_theme?.image_search_button_disabled_background || '#e0e0e0',
                  color: config?.ui_theme?.image_search_button_disabled_icon || '#9e9e9e',
                },
              }}
            >
              <PhotoCamera />
            </IconButton>
          </span>
        </Tooltip>

        {/* Image Source Menu */}
        <Menu
          anchorEl={imageMenuAnchor}
          open={Boolean(imageMenuAnchor)}
          onClose={handleImageMenuClose}
          disableScrollLock={true}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
        >
          <MenuItem onClick={handleBrowseFiles}>
            <ListItemIcon>
              <PhotoLibrary fontSize="small" />
            </ListItemIcon>
            <ListItemText>Browse Files</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleCameraClick}>
            <ListItemIcon>
              <CameraAlt fontSize="small" />
            </ListItemIcon>
            <ListItemText>Take Photo</ListItemText>
          </MenuItem>
        </Menu>
        </>)}

        {/* Text Input with Image Thumbnail */}
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder={getLocalizedPlaceholder(
            textMessage,
            config?.ui_text?.text_input_placeholder
          )}
          value={textMessage}
          onChange={(e) => setTextMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isDisabled}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: selectedImage ? (
              <InputAdornment position="start" sx={{ ml: -0.5, mr: 0.5 }}>
                <Chip
                  avatar={
                    <Avatar
                      src={selectedImage.preview}
                      alt="Selected"
                      sx={{ width: 28, height: 28 }}
                    />
                  }
                  label="Image"
                  onDelete={handleRemoveImage}
                  deleteIcon={
                    <Tooltip title={config?.ui_tooltips?.image_search_remove_tooltip || 'Remove image'}>
                      <Cancel />
                    </Tooltip>
                  }
                  size="small"
                  sx={{
                    height: 28,
                    '& .MuiChip-avatar': {
                      width: 28,
                      height: 28,
                      marginLeft: 0,
                    },
                    '& .MuiChip-label': {
                      paddingLeft: 1,
                      paddingRight: 1,
                      fontSize: '0.75rem',
                    },
                    '& .MuiChip-deleteIcon': {
                      fontSize: '1rem',
                      marginRight: 0.5,
                    },
                  }}
                />
              </InputAdornment>
            ) : undefined,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: config?.ui_theme?.text_input_background || '#ffffff',
              '& fieldset': {
                borderColor: config?.ui_theme?.text_input_border || '#cccccc',
              },
              '&:hover fieldset': {
                borderColor: config?.ui_theme?.text_input_border || '#cccccc',
              },
              '&.Mui-focused fieldset': {
                borderColor: config?.ui_theme?.text_input_border_focus || '#1976d2',
              },
            },
            '& .MuiInputBase-input': {
              ...getTextDirectionStyles(textMessage),
              color: config?.ui_theme?.text_input_text || '#000000',
            },
          }}
        />
          {/* Send Button */}
        <Tooltip title={config?.ui_tooltips?.send_button_tooltip || "Send your message"}>
          <span>
            <Button
              variant="contained"
              endIcon={<Send />}
              onClick={handleSendTextMessage}
              disabled={isDisabled || (!textMessage.trim() && !selectedImage)}
              sx={{
                minWidth: isMobile ? 40 : 60,
                height: 36,
                borderRadius: 3,
                flexShrink: 0,
                backgroundColor: config?.ui_theme?.send_button_background || '#1976d2',
                '&:hover': {
                  backgroundColor: `${config?.ui_theme?.send_button_hover_background || '#1565c0'} !important`,
                },
                '&.Mui-disabled': {
                  backgroundColor: '#cccccc',
                  color: '#666666',
                },
              }}>
              {!isMobile && (config?.ui_text?.send_button_label || "Send")}
            </Button>          </span>
        </Tooltip>
      </Box>      {/* AI Disclaimer */}
      <Typography 
        variant="caption" 
        sx={{ 
          textAlign: 'center', 
          color: '#666666', 
          fontSize: '0.65rem',
          mt: 0.25,
          px: 0.5,
          display: 'block',
          width: '100%'
        }}
      >
        {config?.ui_text?.disclaimer_text || "Content is generated by AI. Double-check against references."}
      </Typography>
    </Box>
  );
};

export default AudioControls;